import { apiHelpers } from '@/services/api';
import {
  WatchlistVideo,
  WatchlistVideoComment,
  WatchSession,
  WatchlistFilters,
  WatchSessionFilters,
  CreateWatchlistVideoInput,
  UpdateWatchlistVideoInput,
  RateVideoInput,
  AddVideoCommentInput,
  CreateWatchSessionInput,
  UpdateWatchSessionInput,
  UpdatePlaybackInput,
  PaginatedWatchlistVideosResponse,
  PaginatedVideoCommentsResponse,
  PaginatedWatchSessionsResponse,
  RandomVideoFilters,
} from '@/types/watchlist.types';

// Watchlist Service
export const watchlistService = {
  // ==============================================
  // Video Operations
  // ==============================================

  /**
   * Get list of watchlist videos with filters
   */
  async getVideos(filters?: WatchlistFilters): Promise<PaginatedWatchlistVideosResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.platform) params.platform = filters.platform;
      if (filters.addedBy) params.addedBy = filters.addedBy;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.search) params.search = filters.search;
    }

    return apiHelpers.get<PaginatedWatchlistVideosResponse>('/watchlist', params);
  },

  /**
   * Get single video by ID
   */
  async getVideoById(id: string): Promise<WatchlistVideo> {
    return apiHelpers.get<WatchlistVideo>(`/watchlist/${id}`);
  },

  /**
   * Add a new video to the watchlist
   */
  async createVideo(data: CreateWatchlistVideoInput): Promise<WatchlistVideo> {
    return apiHelpers.post<WatchlistVideo>('/watchlist', data);
  },

  /**
   * Update a video
   */
  async updateVideo(id: string, data: UpdateWatchlistVideoInput): Promise<WatchlistVideo> {
    return apiHelpers.put<WatchlistVideo>(`/watchlist/${id}`, data);
  },

  /**
   * Delete a video
   */
  async deleteVideo(id: string): Promise<void> {
    return apiHelpers.delete(`/watchlist/${id}`);
  },

  // ==============================================
  // Rating Operations
  // ==============================================

  /**
   * Rate a video (1-10)
   */
  async rateVideo(id: string, data: RateVideoInput): Promise<WatchlistVideo> {
    return apiHelpers.post<WatchlistVideo>(`/watchlist/${id}/rate`, data);
  },

  /**
   * Remove rating from a video
   */
  async removeRating(id: string): Promise<WatchlistVideo> {
    return apiHelpers.delete<WatchlistVideo>(`/watchlist/${id}/rate`);
  },

  // ==============================================
  // Comment Operations
  // ==============================================

  /**
   * Get comments for a video
   */
  async getComments(videoId: string, page = 1, limit = 20): Promise<PaginatedVideoCommentsResponse> {
    return apiHelpers.get<PaginatedVideoCommentsResponse>(`/watchlist/${videoId}/comments`, { page, limit });
  },

  /**
   * Add a comment to a video
   */
  async addComment(videoId: string, data: AddVideoCommentInput): Promise<WatchlistVideoComment> {
    return apiHelpers.post<WatchlistVideoComment>(`/watchlist/${videoId}/comments`, data);
  },

  /**
   * Delete a comment
   */
  async deleteComment(videoId: string, commentId: string): Promise<void> {
    return apiHelpers.delete(`/watchlist/${videoId}/comments/${commentId}`);
  },

  // ==============================================
  // Watch Session Operations
  // ==============================================

  /**
   * Create a watch session for a video
   */
  async createWatchSession(videoId: string, data: CreateWatchSessionInput): Promise<WatchSession> {
    return apiHelpers.post<WatchSession>(`/watchlist/${videoId}/watch-session`, data);
  },

  /**
   * Get list of watch sessions with filters
   */
  async getWatchSessions(filters?: WatchSessionFilters): Promise<PaginatedWatchSessionsResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
      if (filters.hostId) params.hostId = filters.hostId;
      if (filters.videoId) params.videoId = filters.videoId;
      if (filters.upcoming !== undefined) params.upcoming = filters.upcoming;
    }

    return apiHelpers.get<PaginatedWatchSessionsResponse>('/watch-sessions', params);
  },

  /**
   * Get single watch session by ID
   */
  async getWatchSessionById(sessionId: string): Promise<WatchSession> {
    return apiHelpers.get<WatchSession>(`/watch-sessions/${sessionId}`);
  },

  /**
   * Update a watch session
   */
  async updateWatchSession(sessionId: string, data: UpdateWatchSessionInput): Promise<WatchSession> {
    return apiHelpers.put<WatchSession>(`/watch-sessions/${sessionId}`, data);
  },

  /**
   * Join a watch session
   */
  async joinWatchSession(sessionId: string): Promise<WatchSession> {
    return apiHelpers.post<WatchSession>(`/watch-sessions/${sessionId}/join`, {});
  },

  /**
   * Leave a watch session
   */
  async leaveWatchSession(sessionId: string): Promise<WatchSession> {
    return apiHelpers.post<WatchSession>(`/watch-sessions/${sessionId}/leave`, {});
  },

  /**
   * Update playback state (for real-time sync)
   */
  async updatePlayback(sessionId: string, data: UpdatePlaybackInput): Promise<WatchSession> {
    return apiHelpers.put<WatchSession>(`/watch-sessions/${sessionId}/playback`, data);
  },

  // ==============================================
  // Watch History Operations
  // ==============================================

  /**
   * Mark a video as watched
   */
  async markAsWatched(videoId: string): Promise<void> {
    return apiHelpers.post(`/watchlist/${videoId}/watch`, {});
  },

  /**
   * Unmark a video as watched
   */
  async unmarkAsWatched(videoId: string): Promise<void> {
    return apiHelpers.delete(`/watchlist/${videoId}/watch`);
  },

  /**
   * Get a random unwatched video
   */
  async getRandomVideo(filters?: RandomVideoFilters): Promise<WatchlistVideo> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.platform) params.platform = filters.platform;
      if (filters.unwatchedOnly !== undefined) params.unwatchedOnly = filters.unwatchedOnly;
      if (filters.minRating) params.minRating = filters.minRating;
    }

    return apiHelpers.get<WatchlistVideo>('/watchlist/random', params);
  },
};

export default watchlistService;
